import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import AdminPage from './pages/AdminPage';
import AircraftGuidePage from './pages/AircraftGuidePage';
import EmptyLegsPage from './pages/EmptyLegsPage';
import HomePage from './pages/HomePage';
import LocationsPage from './pages/LocationsPage';
import RequestCharterPage from './pages/RequestCharterPage';

const navItems = [
  { href: '#home', label: 'Home' },
  { href: '#empty-legs', label: 'See Empty Legs' },
  { href: '#locations', label: 'Our Locations' },
  { href: '#aircraft-guide', label: 'Aircraft Guide' },
  { href: '#why-aquila', label: 'Why Aquila?' },
];

const supportedPaths = new Set([
  '/',
  '/request-charter',
  '/empty-legs',
  '/locations',
  '/aircraft-guide',
  '/admin',
]);

const normalizePath = (path) => {
  if (!path) {
    return '/';
  }

  const sanitizedPath = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  return supportedPaths.has(sanitizedPath) ? sanitizedPath : '/';
};

const isModifiedEvent = (event) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0;

function Taskbar({ leftNavItems, rightNavItems, onLinkClick, taskbarRef, useDarkNavText }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const allNavItems = useMemo(() => [...leftNavItems, ...rightNavItems], [leftNavItems, rightNavItems]);

  const handleTaskbarLinkClick = (event, href) => {
    const shouldCloseMenu = !isModifiedEvent(event) && !event.defaultPrevented;
    onLinkClick(event, href);
    if (shouldCloseMenu) {
      setIsMenuOpen(false);
    }
  };

  return (
    <nav
      ref={taskbarRef}
      className={`taskbar ${useDarkNavText ? 'taskbar--dark-text' : ''} ${isMenuOpen ? 'taskbar--menu-open' : ''}`}
      aria-label="Main navigation"
    >
      <div className="taskbar-inner">
        <ul className="taskbar-links taskbar-links-left">
          {leftNavItems.map((item) => (
            <li key={item.href}>
              <a className="taskbar-link" href={item.href} onClick={(event) => handleTaskbarLinkClick(event, item.href)}>
                {item.label}
              </a>
            </li>
          ))}
        </ul>

        <a
          className="taskbar-logo-link"
          href="#home"
          aria-label="Aquila Charters Home"
          onClick={(event) => handleTaskbarLinkClick(event, '#home')}
        >
          <img className="taskbar-logo" src="/assets/logovector-nav.svg" alt="Aquila Charters" />
        </a>

        <button
          className="taskbar-menu-button"
          type="button"
          aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation"
          onClick={() => setIsMenuOpen((current) => !current)}
        >
          <span />
          <span />
          <span />
        </button>

        <div className="taskbar-actions">
          <ul className="taskbar-links taskbar-links-right">
            {rightNavItems.map((item) => (
              <li key={item.href}>
                <a className="taskbar-link" href={item.href} onClick={(event) => handleTaskbarLinkClick(event, item.href)}>
                  {item.label}
                </a>
              </li>
            ))}
            <li>
              <a
                className="taskbar-link taskbar-cta"
                href="#request-charter"
                onClick={(event) => handleTaskbarLinkClick(event, '#request-charter')}
              >
                Request a Charter
              </a>
            </li>
          </ul>
        </div>

        <div className="taskbar-mobile-panel" id="mobile-navigation">
          {allNavItems.map((item) => (
            <a
              key={item.href}
              className="taskbar-mobile-link"
              href={item.href}
              onClick={(event) => handleTaskbarLinkClick(event, item.href)}
            >
              {item.label}
            </a>
          ))}
          <a
            className="taskbar-mobile-cta"
            href="#request-charter"
            onClick={(event) => handleTaskbarLinkClick(event, '#request-charter')}
          >
            Request a Charter
          </a>
        </div>
      </div>
    </nav>
  );
}

function SiteFooter() {
  return (
    <footer className="footer">
      <div className="footer-grid">
        <div className="footer-logo-wrap">
          <img src="/assets/aquila-logo.png" alt="Aquila Charters logo" />
        </div>

        <div className="footer-col">
          <h3>AIRCRAFT</h3>
          <p>Helicopter</p>
          <p>Light Jets</p>
          <p>Midsize Jets</p>
          <p>Heavy Jets</p>
        </div>

        <div className="footer-col">
          <h3>AQUILA CHARTERS</h3>
          <p>
            Aquila Charters, a South African private air charter broker. Aquila Charters does not own or operate any
            aircraft. All flights are conducted by registered South African Part 121 or Part 135 Operators, or their
            approved foreign equivalents, who meet and exceed the standards set by the SACAA and the internal safety and
            operational requirements of Aquila Charters.
          </p>
        </div>

        <div className="footer-col">
          <h3>CONTACT US</h3>
          <p>081 876 5024</p>
          <p className="footer-label">GENERAL ENQUIRIES</p>
          <p>enquiry@aquilacharters.co.za</p>
          <p className="footer-label">SALES ENQUIRIES</p>
          <p>sales@aquilacharters.co.za</p>
          <p className="footer-label">ADVERTISE YOUR EMPTY LEG</p>
          <p>partners@aquilacharters.co.za</p>
        </div>
      </div>
      <div className="footer-disclaimer">@2026 All rights reserved. Aquila Charters Inc.</div>
    </footer>
  );
}

function App() {
  const taskbarRef = useRef(null);
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));
  const [useDarkNavText, setUseDarkNavText] = useState(false);

  const leftNavItems = useMemo(() => navItems.slice(0, 3), []);
  const rightNavItems = useMemo(() => navItems.slice(3), []);

  const navigate = useCallback((nextPath) => {
    const normalizedPath = normalizePath(nextPath);
    const currentPath = normalizePath(window.location.pathname);

    if (normalizedPath !== currentPath) {
      window.history.pushState({}, '', normalizedPath);
    }

    window.scrollTo({ top: 0, behavior: 'auto' });
    setPath(normalizedPath);
  }, []);

  const scrollToHash = useCallback((hash, behavior = 'smooth') => {
    const targetId = hash.replace('#', '');
    const targetElement = document.getElementById(targetId);
    if (!targetElement) {
      return;
    }

    const targetTop = targetElement.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(0, targetTop), behavior });

    if (window.location.hash !== hash) {
      window.history.replaceState({}, '', hash);
    }
  }, []);

  const navigateToHomeHash = useCallback(
    (hash) => {
      if (path === '/') {
        scrollToHash(hash, 'smooth');
        return;
      }

      window.history.pushState({}, '', `/${hash}`);
      setPath('/');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollToHash(hash, 'auto');
        });
      });
    },
    [path, scrollToHash]
  );

  useEffect(() => {
    const handlePopState = () => {
      setPath(normalizePath(window.location.pathname));
      window.scrollTo({ top: 0, behavior: 'auto' });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (path !== '/') {
      setUseDarkNavText(false);
    }
  }, [path]);

  useEffect(() => {
    if (path !== '/') {
      return;
    }

    if (window.location.hash) {
      window.requestAnimationFrame(() => scrollToHash(window.location.hash, 'auto'));
    }
  }, [path, scrollToHash]);

  const handleLinkClick = useCallback(
    (event, href) => {
      if (isModifiedEvent(event) || event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      if (href.startsWith('#')) {
        navigateToHomeHash(href);
        return;
      }
      navigate(href);
    },
    [navigate, navigateToHomeHash]
  );

  const page = (() => {
    if (path === '/request-charter') {
      return <RequestCharterPage />;
    }
    if (path === '/empty-legs') {
      return <EmptyLegsPage />;
    }
    if (path === '/locations') {
      return <LocationsPage />;
    }
    if (path === '/aircraft-guide') {
      return <AircraftGuidePage />;
    }
    if (path === '/admin') {
      return <AdminPage />;
    }

    return <HomePage navigate={navigate} taskbarRef={taskbarRef} onNavTextChange={setUseDarkNavText} />;
  })();

  return (
    <div className={`site ${path === '/' ? '' : 'site--inner-pages'}`}>
      <Taskbar
        leftNavItems={leftNavItems}
        rightNavItems={rightNavItems}
        onLinkClick={handleLinkClick}
        taskbarRef={taskbarRef}
        useDarkNavText={path === '/' && useDarkNavText}
      />

      <main className={`site-main ${path === '/' ? '' : 'site-main--page'}`}>{page}</main>
      <SiteFooter />
    </div>
  );
}

export default App;
